import React, { useState, useEffect } from 'react';
import { getSuccessStories, saveSuccessStory, deleteSuccessStory } from '../services/apiService';
import { supabase } from '../services/supabase';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  GraduationCap, 
  Building2, 
  BookOpen, 
  Loader2, 
  Upload,
  Image as ImageIcon 
} from 'lucide-react';

const AdminSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const [editingStory, setEditingStory] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    course: '',
    university: '',
    story: '',
    photo: null,
    category: 'visa', // Default to visa
  });

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      setIsLoading(true);
      const data = await getSuccessStories();
      setStories(data);
    } catch (error) {
      console.error('Error loading stories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo: file });
      // Use FileReader for preview (data URL, not blob URL)
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let photoUrl = editingStory?.photo || editingStory?.photo_url || editingStory?.imageUrl;

      if (formData.photo) {
        const fileExt = formData.photo.name.split('.').pop();
        const fileName = `story_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `success-stories/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, formData.photo);

        if (uploadError) {
          console.error('Photo upload error:', uploadError);
          showToast('Failed to upload photo. Please try again.', 'error');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath);
        photoUrl = publicUrl;
      }

      await saveSuccessStory({
        name: formData.name,
        course: formData.course,
        university: formData.university,
        story: formData.story,
        category: formData.category,
        photo_url: photoUrl,
        id: editingStory?.id,
      });
      
      handleCloseForm();
      loadStories();
      showToast(editingStory ? 'Story updated successfully!' : 'Story added successfully!', 'success');
    } catch (error) {
      console.error('Error saving story:', error);
      showToast('Error saving story. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (story) => {
    setEditingStory(story);
    setFormData({
      name: story.name || story.studentName || '',
      course: story.course || story.program || '',
      university: story.university || story.institution || '',
      story: story.story || '',
      photo: null,
      category: story.category || 'general',
    });
    setPreviewUrl(story.photo || story.photo_url || story.imageUrl || null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this story?')) {
      try {
        await deleteSuccessStory(id);
        loadStories();
        showToast('Story deleted successfully!', 'success');
      } catch (error) {
        console.error('Error deleting story:', error);
        showToast('Error deleting story. Please try again.', 'error');
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingStory(null);
    setFormData({
      name: '', course: '', university: '', story: '', photo: null, category: 'visa',
    });
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-8">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-2xl font-bold text-slate-900">Success Stories</h1>
            <p className="text-slate-500 text-sm mt-1">Showcase student achievements and visa approvals.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
        >
          <Plus size={18} />
          Add Story
        </button>
      </div>

      {/* STORIES GRID */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
          <p className="text-slate-500">Loading stories...</p>
        </div>
      ) : stories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((story) => (
            <div key={story.id} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col h-full overflow-hidden group">
              
              {/* Image Area */}
              <div className="h-48 bg-slate-100 overflow-hidden relative">
                {(() => {
                  const url = story.photo || story.photo_url || story.imageUrl;
                  const safeUrl = url && !url.startsWith('blob:') ? url : null;
                  return safeUrl ? (
                    <img
                      src={safeUrl}
                      alt={story.name || story.studentName}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                {(() => {
                  const url = story.photo || story.photo_url || story.imageUrl;
                  const hasValidPhoto = url && !url.startsWith('blob:');
                  return !hasValidPhoto ? (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                      <GraduationCap size={48} className="text-slate-300" />
                    </div>
                  ) : null;
                })()}
                
                {/* Category Badge */}
                <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${
                        story.category === 'visa' 
                        ? 'bg-green-100 text-green-700 border-green-200' 
                        : story.category === 'ielts'
                        ? 'bg-blue-100 text-blue-700 border-blue-200'
                        : 'bg-white/90 text-slate-700 border-white/50 backdrop-blur-sm'
                    }`}>
                        {story.category === 'visa' ? 'Visa Success' : story.category === 'ielts' ? 'IELTS Success' : 'Alumni'}
                    </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  {story.name || story.studentName}
                </h3>
                
                <div className="space-y-1 mb-4">
                    {(story.course || story.program) && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <BookOpen size={12} className="text-red-500" />
                            <span className="font-medium">{story.course || story.program}</span>
                        </div>
                    )}
                    {(story.university || story.institution) && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Building2 size={12} className="text-red-500" />
                            <span>{story.university || story.institution}</span>
                        </div>
                    )}
                </div>

                <p className="text-slate-600 text-sm line-clamp-3 mb-4 flex-1">
                  {story.story || 'No story details provided.'}
                </p>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-slate-50 mt-auto">
                  <button
                    onClick={() => handleEdit(story)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg text-xs font-medium transition-colors"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(story.id)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-xs font-medium transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
            <GraduationCap size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-900">No stories yet</h3>
          <p className="text-slate-500 mt-1">Add a success story to inspire new students.</p>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h2 className="text-lg font-bold text-slate-900">
                {editingStory ? 'Edit Success Story' : 'New Success Story'}
              </h2>
              <button onClick={handleCloseForm} className="text-slate-400 hover:text-red-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="p-6 overflow-y-auto">
                <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Photo Upload */}
                <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                        {previewUrl ? (
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                            <ImageIcon size={24} className="text-slate-300" />
                        )}
                    </div>
                    <label className="flex-1">
                        <span className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Student Photo</span>
                        <div className="flex items-center gap-2 w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors relative overflow-hidden group">
                            <Upload size={16} className="text-slate-400 group-hover:text-slate-600" />
                            <span className="text-sm text-slate-500 group-hover:text-slate-700">Choose file...</span>
                            <input type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                        </div>
                    </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Student Name</label>
                        <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                            placeholder="John Doe"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Story Type</label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="category"
                                    value="visa"
                                    checked={formData.category === 'visa'}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-red-600 focus:ring-red-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-slate-700">Visa Success Stories</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="category"
                                    value="ielts"
                                    checked={formData.category === 'ielts'}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-red-600 focus:ring-red-500 focus:ring-2"
                                />
                                <span className="text-sm font-medium text-slate-700">IELTS Success Stories</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Course / Program</label>
                        <input
                            type="text"
                            name="course"
                            value={formData.course}
                            onChange={handleChange}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                            placeholder="e.g. MS in CS"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">University / Institution</label>
                        <input
                            type="text"
                            name="university"
                            value={formData.university}
                            onChange={handleChange}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                            placeholder="e.g. University of Toronto"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Story / Testimonial</label>
                    <textarea
                        name="story"
                        required
                        rows="5"
                        value={formData.story}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm resize-none"
                        placeholder="Share the details of their success..."
                    />
                </div>

                <div className="pt-2 flex gap-3">
                    <button
                        type="button"
                        onClick={handleCloseForm}
                        disabled={saving}
                        className="flex-1 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 bg-slate-900 text-white py-2.5 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : (editingStory ? 'Update Story' : 'Save Story')}
                    </button>
                </div>
                </form>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default AdminSuccessStories;